import 'app/components/Dropzone/ExportStudyModal/style.scss';

import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Popup } from 'semantic-ui-react';
import ModalWithSideImagePreview, {
  useImagePreview,
} from 'app/components/ModalWithSideImagePreview';

import ImagesSelector, { ImageId, SelectableImage } from 'app/components/ImagesSelector';
import { selectPacsExportSettings, setPacsExportAnnotations } from 'app/redux/exportSettings';
import {
  selectViewerConfiguration,
  updateSyncToPACSOnValidate,
} from 'app/redux/viewerConfiguration/reducer';

export type PACSExportOptions = {
  isExportAllAnnotations: boolean;
};

export type PACSImagesSelectorProps<T extends SelectableImage> = {
  images: T[];
  initialSelectedImages: T[];
  onConfirm: (selectedImages: T[], exportOptions: PACSExportOptions) => void;
};

function PACSImagesSelectorContent<T extends SelectableImage>({
  images,
  initialSelectedImages,
  onConfirm,
  onCancel,
}: PACSImagesSelectorProps<T> & { onCancel: () => void }) {
  const viewerConfiguration = useSelector(selectViewerConfiguration);
  const pacsExportSettings = useSelector(selectPacsExportSettings);
  const { openPreview } = useImagePreview();
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState<ImageId[]>([]);
  const prevInitialSelectedImageIds = useRef<ImageId[]>();
  const { syncToPACSOnValidate } = viewerConfiguration;
  const { isExportAllAnnotations } = pacsExportSettings;

  const handleImageClick = (imageId: number | string) => {
    const imageIndex = images.findIndex((img) => img.id === imageId);
    if (imageIndex !== -1) {
      openPreview(imageIndex);
    }
  };

  const onImagesSelect = (imageIds: ImageId[]) => {
    setSelectedImages(imageIds);
  };

  const toggleSyncToPACSOnValidateChange = () => {
    dispatch(updateSyncToPACSOnValidate(!syncToPACSOnValidate));
  };

  const toggleExportAnnotations = () => {
    dispatch(setPacsExportAnnotations(!isExportAllAnnotations));
  };

  const triggerConfirm = () => {
    onConfirm(
      _.filter(images, ({ id }) => selectedImages.includes(id)),
      { isExportAllAnnotations }
    );
  };

  const initialSelectedImageIds = initialSelectedImages?.map(({ id }) => id);
  const isSameInitialImages = _.isEqual(
    initialSelectedImageIds,
    prevInitialSelectedImageIds.current
  );

  if (!isSameInitialImages) prevInitialSelectedImageIds.current = initialSelectedImageIds;

  useEffect(() => {
    setSelectedImages(prevInitialSelectedImageIds.current);
  }, [prevInitialSelectedImageIds.current]);

  return (
    <>
      <ModalWithSideImagePreview.Header className="pacs-images-selector__header">
        <FormattedMessage id="pacsImagesSelector.title" />
      </ModalWithSideImagePreview.Header>
      <ModalWithSideImagePreview.Content
        className="pacs-images-selector__content"
        data-testid="modal-content"
      >
        <form className="exportForm">
          <fieldset>
            <legend>
              <FormattedMessage id="exportStudyModal.selectImages.label" />
            </legend>
            <ImagesSelector
              onSelect={onImagesSelect}
              images={images}
              selectedImagesIds={selectedImages}
              onImageClick={handleImageClick}
            />
            <div className="checkbox">
              <input
                type="checkbox"
                id="export-annotations"
                name="export-annotations"
                data-testid="export-annotations"
                checked={isExportAllAnnotations}
                onChange={toggleExportAnnotations}
              />
              <label htmlFor="export-annotations">
                <FormattedMessage id="general.export.includeAnnotations" />
              </label>
            </div>
          </fieldset>

          <div>
            <div className="checkbox">
              <input
                type="checkbox"
                id="syncOnValidate-checkbox"
                name="syncOnValidate-checkbox"
                checked={syncToPACSOnValidate}
                onChange={toggleSyncToPACSOnValidateChange}
              />
              <label htmlFor="syncOnValidate-checkbox">
                <FormattedMessage id="dropzone.pacsSend.syncOnValidate.checkbox" />
              </label>
            </div>
          </div>
        </form>
      </ModalWithSideImagePreview.Content>
      <ModalWithSideImagePreview.Actions className="pacs-images-selector__footer">
        <Button
          data-testid="button-export"
          type="button"
          positive
          className="footerModal__button footerModal__button--green"
          onClick={triggerConfirm}
        >
          <FormattedMessage id="general.confirm" />
        </Button>

        <Button type="button" secondary className="footerModal__button" onClick={onCancel}>
          <FormattedMessage id="general.cancel" />
        </Button>
      </ModalWithSideImagePreview.Actions>
    </>
  );
}

export default function PACSImagesSelector<T extends SelectableImage>({
  images,
  initialSelectedImages,
  onConfirm,
}: PACSImagesSelectorProps<T>) {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const onConfirmWrapper = useCallback(
    (selectedImages: T[], exportOptions: PACSExportOptions) => {
      onConfirm(selectedImages, exportOptions);
      setIsSelectorOpen(false);
    },
    [onConfirm]
  );
  const onCancel = useCallback(() => {
    setIsSelectorOpen(false);
  }, []);

  return (
    <>
      <Popup
        trigger={
          <button
            className="picoxia"
            type="button"
            data-testid="pacs-modal-trigger"
            onClick={() => setIsSelectorOpen(true)}
          >
            <Icon className="picoxia-fix" inverted name="cloud upload" size="big" />
          </button>
        }
        content={<FormattedMessage id="dropzone.pacsSend.description" />}
      />
      <ModalWithSideImagePreview
        previewableImages={images}
        className="dark-mode"
        onClose={() => setIsSelectorOpen(false)}
        onOpen={() => setIsSelectorOpen(true)}
        open={isSelectorOpen}
        mainCardClassName="pacs-images-selector"
        previewCardClassName="pacs-images-selector"
      >
        <PACSImagesSelectorContent
          images={images}
          initialSelectedImages={initialSelectedImages}
          onConfirm={onConfirmWrapper}
          onCancel={onCancel}
        />
      </ModalWithSideImagePreview>
    </>
  );
}
